/**
 * WordPress dependencies
 */
import { ToolbarButton, Toolbar, ToolbarGroup, __unstableMotion as motion, __unstableAnimatePresence as AnimatePresence } from '@wordpress/components';
import { useMemo, useState, useRef } from '@wordpress/element';
import { _n, sprintf, __ } from '@wordpress/i18n';
import { closeSmall } from '@wordpress/icons';
import { useReducedMotion } from '@wordpress/compose';
import { useRegistry } from '@wordpress/data';

/**
 * Internal dependencies
 */
import { ActionWithModal } from './item-actions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SNACKBAR_VARIANTS = {
  init: {
    bottom: -48
  },
  open: {
    bottom: 24,
    transition: {
      bottom: {
        type: 'tween',
        duration: 0.2,
        ease: [0, 0, 0.2, 1]
      }
    }
  },
  exit: {
    opacity: 0,
    bottom: 24,
    transition: {
      opacity: {
        type: 'tween',
        duration: 0.2,
        ease: [0, 0, 0.2, 1]
      }
    }
  }
};
function ActionTrigger({
  action,
  onClick,
  isBusy,
  items
}) {
  const label = typeof action.label === 'string' ? action.label : action.label(items);
  return /*#__PURE__*/_jsx(ToolbarButton, {
    disabled: isBusy,
    label: label,
    icon: action.icon,
    isDestructive: action.isDestructive,
    size: "compact",
    onClick: onClick,
    isBusy: isBusy,
    tooltipPosition: "top"
  });
}
const EMPTY_ARRAY = [];
function ActionButton({
  action,
  selectedItems,
  actionInProgress,
  setActionInProgress
}) {
  const registry = useRegistry();
  const selectedEligibleItems = useMemo(() => {
    return selectedItems.filter(item => {
      return !action.isEligible || action.isEligible(item);
    });
  }, [action, selectedItems]);
  if ('RenderModal' in action) {
    return /*#__PURE__*/_jsx(ActionWithModal, {
      action: action,
      items: selectedEligibleItems,
      ActionTrigger: ActionTrigger
    }, action.id);
  }
  return /*#__PURE__*/_jsx(ActionTrigger, {
    action: action,
    onClick: () => {
      setActionInProgress(action.id);
      action.callback(selectedItems, {
        registry
      });
    },
    items: selectedEligibleItems,
    isBusy: actionInProgress === action.id
  }, action.id);
}
function renderToolbarContent(selection, actionsToShow, selectedItems, actionInProgress, setActionInProgress, onSelectionChange) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ToolbarGroup, {
      children: /*#__PURE__*/_jsx("div", {
        className: "dataviews-bulk-actions__selection-count",
        children: selection.length === 1 ? __('1 item selected') : sprintf(
        // translators: %s: Total number of selected items.
        _n('%s item selected', '%s items selected', selection.length), selection.length)
      })
    }), /*#__PURE__*/_jsx(ToolbarGroup, {
      children: actionsToShow.map(action => {
        return /*#__PURE__*/_jsx(ActionButton, {
          action: action,
          selectedItems: selectedItems,
          actionInProgress: actionInProgress,
          setActionInProgress: setActionInProgress
        }, action.id);
      })
    }), /*#__PURE__*/_jsx(ToolbarGroup, {
      children: /*#__PURE__*/_jsx(ToolbarButton, {
        icon: closeSmall,
        showTooltip: true,
        tooltipPosition: "top",
        label: __('Cancel'),
        disabled: !!actionInProgress,
        onClick: () => {
          onSelectionChange(EMPTY_ARRAY);
        }
      })
    })]
  });
}
function ToolbarContent({
  selection,
  actionsToShow,
  selectedItems,
  onSelectionChange
}) {
  const [actionInProgress, setActionInProgress] = useState(null);
  const buttons = useRef(null);
  if (!actionInProgress) {
    if (buttons.current) {
      buttons.current = null;
    }
    return renderToolbarContent(selection, actionsToShow, selectedItems, actionInProgress, setActionInProgress, onSelectionChange);
  } else if (!buttons.current) {
    buttons.current = renderToolbarContent(selection, actionsToShow, selectedItems, actionInProgress, setActionInProgress, onSelectionChange);
  }
  return buttons.current;
}
export default function BulkActionsToolbar({
  data,
  selection,
  actions = EMPTY_ARRAY,
  onSelectionChange,
  getItemId
}) {
  const isReducedMotion = useReducedMotion();
  const selectedItems = useMemo(() => {
    return data.filter(item => selection.includes(getItemId(item)));
  }, [selection, data, getItemId]);
  const actionsToShow = useMemo(() => actions.filter(action => {
    return action.supportsBulk && action.icon && selectedItems.some(item => !action.isEligible || action.isEligible(item));
  }), [actions, selectedItems]);
  if (selection && selection.length === 0 || actionsToShow.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(AnimatePresence, {
    children: /*#__PURE__*/_jsx(motion.div, {
      layout: !isReducedMotion // See https://www.framer.com/docs/animation/#layout-animations
      ,
      initial: "init",
      animate: "open",
      exit: "exit",
      variants: isReducedMotion ? undefined : SNACKBAR_VARIANTS,
      className: "dataviews-bulk-actions",
      children: /*#__PURE__*/_jsx(Toolbar, {
        label: __('Bulk actions'),
        children: /*#__PURE__*/_jsx("div", {
          className: "dataviews-bulk-actions-toolbar-wrapper",
          children: /*#__PURE__*/_jsx(ToolbarContent, {
            selection: selection,
            actionsToShow: actionsToShow,
            selectedItems: selectedItems,
            onSelectionChange: onSelectionChange
          })
        })
      })
    })
  });
}
//# sourceMappingURL=bulk-actions-toolbar.js.map